/**
 * Bridge Clerk's imperative, promise-returning API into Effect.
 *
 * Clerk's React hooks (`useSignUp`, `useSignIn`, `useClerk`) expose an
 * imperative API whose methods return raw promises. Rather than opting the
 * client-side auth flows out of the codebase's no-`async`/`await` rule, we wrap
 * each Clerk call in {@link clerkTryPromise}, which lifts the promise into an
 * Effect failing with a typed {@link ClerkCallError}. The flows then compose
 * with `Effect.pipe`/`flatMap`/`map` like the rest of the app and run through
 * the effect-atom runtime.
 *
 * @since 1.0.0
 */
import { Data, Effect } from "effect";

/**
 * Typed failure carrying whatever Clerk threw on the failure channel, so call
 * sites can recover (`Effect.catchAll`) and surface a calm, customer-facing
 * message via the existing error-message helpers.
 *
 * @since 1.0.0
 */
export class ClerkCallError extends Data.TaggedError("ClerkCallError")<{
  readonly cause: unknown;
}> {}

/**
 * Lift a single Clerk imperative promise call into an Effect, capturing a
 * rejection as a {@link ClerkCallError}.
 *
 * @since 1.0.0
 */
export const clerkTryPromise = <A>(call: () => Promise<A>): Effect.Effect<A, ClerkCallError> =>
  Effect.tryPromise({ try: call, catch: (cause) => new ClerkCallError({ cause }) });
